import { useCallback, useEffect, useMemo, useState } from 'react';
import IconButton from '~/components/IconButton/IconButton';
import EditorButton from '~/components/editor/EditorButton/EditorButton';
import { Basis, hasXyChannel } from '~/models/studio/channel';
import { useCurrentFrame } from '~/systems/currentFrame';
import { useExperiment } from '~/systems/experiment';
import { useSimulation } from '~/systems/simulation';
import { SampleBasis } from '~/systems/simulation/wasm-pkg/pulser_wasm';
// include wasm in build
import '~/systems/simulation/wasm-pkg/pulser_wasm_bg.wasm?url';
import { clamp } from '~/util';
import type { Run } from '~/systems/simulation/simulation-worker';

import './LocalEmulationControl.css';

const MAX_AUTORUN_ATOMS = 6;
const MAX_SIM_ATOMS = 12;

const enum SimulationRunMode {
  AUTO,
  MANUAL,
  DISABLED
}

type SpeedButton = 'fast-backward' | 'play' | 'fast-forward';

const BUTTON_SPEEDS: Record<SpeedButton, number> = {
  'fast-backward': -5,
  play: 1,
  'fast-forward': 5
};

export default function LocalEmulationControl() {
  const {
    atoms,
    commitedAtoms,
    commitedChannels: channels,
    computedCommittedPulses: computedPulses,
    maxTime
  } = useExperiment();
  const { progress, startSim } = useSimulation();
  const currentFrame = useCurrentFrame();
  const { setAnimationSpeed } = currentFrame;

  const numAtoms = atoms.length;

  const [activeButton, setActiveButton] = useState<SpeedButton | null>(null);

  const hasXyChannels = useMemo(() => hasXyChannel(channels), [channels]);

  const runMode = useMemo((): SimulationRunMode => {
    if (hasXyChannels) {
      return SimulationRunMode.DISABLED;
    }
    if (numAtoms > MAX_SIM_ATOMS) {
      return SimulationRunMode.DISABLED;
    }
    if (numAtoms > MAX_AUTORUN_ATOMS) {
      return SimulationRunMode.MANUAL;
    }
    return SimulationRunMode.AUTO;
  }, [hasXyChannels, numAtoms]);

  const simData = useMemo(() => {
    const reg = commitedAtoms.map((a) => a.pos);
    if (reg.length === 0) {
      return { reg, runs: [] as Run[] };
    }

    const runs: Run[] = [];
    for (const channel of channels) {
      if (channel.basis === Basis.XY) {
        continue;
      }
      const basis =
        channel.basis === Basis.GroundRydberg
          ? SampleBasis.GroundRydberg
          : SampleBasis.Digital;
      const channelRuns: Run[] = [];
      for (const cp of computedPulses) {
        if (cp.pulse.channelUuid !== channel.uuid) {
          continue;
        }
        let address: number | null = null;
        if (cp.pulse.targetUuid != null) {
          address = commitedAtoms.findIndex(
            (a) => a.uuid === cp.pulse.targetUuid
          );
          if (address < 0) {
            continue;
          }
        }
        let run = channelRuns.find(
          (r) => r.basis === basis && r.address === address
        );
        if (run == null) {
          run = { address, basis, pulses: [] };
          channelRuns.push(run);
        }
        run.pulses.push({
          phase: cp.pulse.phase + cp.layout.phaseOffset,
          startTime: cp.layout.time.min,
          amplitude: cp.pulse.amplitude,
          detuning: cp.pulse.detuning
        });
      }
      runs.push(...channelRuns);
    }
    return { reg, runs };
  }, [commitedAtoms, channels, computedPulses]);

  const stopSimPlayback = useCallback(() => {
    setAnimationSpeed(0);
    setActiveButton(null);
  }, [setAnimationSpeed]);

  const stopSim = useCallback(() => {
    startSim([], []);
  }, [startSim]);

  const runSimulation = useCallback(() => {
    startSim(simData.reg as { x: number; y: number }[], simData.runs);
  }, [simData, startSim]);

  useEffect(() => {
    stopSimPlayback();
    if (runMode === SimulationRunMode.AUTO) {
      runSimulation();
    } else {
      stopSim();
    }
  }, [simData, runMode, stopSimPlayback, runSimulation, stopSim]);

  // Watch animationRunning
  useEffect(() => {
    if (!currentFrame.animationRunning) {
      stopSimPlayback();
    }
  }, [currentFrame.animationRunning, stopSimPlayback]);

  function toggleSimPlayback(button: SpeedButton) {
    if (activeButton === button) {
      stopSimPlayback();
      return;
    }
    const time = currentFrame.currentTime;
    const animationSpeed = BUTTON_SPEEDS[button];
    const { playableEnd } = currentFrame;
    if (animationSpeed > 0 && time >= playableEnd) {
      currentFrame.setCurrentTime(0);
    } else if (animationSpeed < 0 && time <= 0) {
      currentFrame.setCurrentTime(playableEnd);
    }
    currentFrame.setAnimationSpeed(animationSpeed);
    setActiveButton(button);
  }

  const currentTime = currentFrame.currentTime;

  const simPlayable = useMemo(() => {
    return progress.done
      ? progress.ticksDone !== 1
      : progress.ticksDone > currentTime;
  }, [currentTime, progress.done, progress.ticksDone]);

  const simPlayCanReverse = useMemo(
    () => !(simPlayable && currentTime > 0),
    [currentTime, simPlayable]
  );

  const simulatedMaxTime = useMemo(
    () => progress.ticksDone - 1,
    [progress.ticksDone]
  );

  const simProgressText = useMemo(() => {
    if (progress.done && progress.ticksDone <= 1) {
      return '';
    }
    const doneFactor = clamp((progress.ticksDone - 1) / (maxTime + 1), 0, 1);
    return `(${Math.round(doneFactor * 100)}%)`;
  }, [maxTime, progress.done, progress.ticksDone]);

  return (
    <div className="LocalEmulationControl">
      {hasXyChannels && <span>No emulation for XY channels</span>}
      {!hasXyChannels && runMode === SimulationRunMode.AUTO && (
        <span>Instant emulation for ≤6 atoms</span>
      )}
      {!hasXyChannels && runMode === SimulationRunMode.MANUAL && (
        <span>Run emulation for ≤12 atoms</span>
      )}
      {!hasXyChannels && runMode === SimulationRunMode.DISABLED && (
        <span>No emulation for &gt;12 atoms</span>
      )}
      <div className="LocalEmulationControl-buttonsRow">
        {runMode === SimulationRunMode.MANUAL && (
          <EditorButton kind="primary" onClick={runSimulation}>
            Emulate {simProgressText}
          </EditorButton>
        )}
        {runMode === SimulationRunMode.AUTO && (
          <EditorButton kind="primary" active disabled>
            Automatic emulation {simProgressText}
          </EditorButton>
        )}
        {runMode === SimulationRunMode.DISABLED && (
          <EditorButton kind="primary" disabled onClick={runSimulation}>
            Emulate
          </EditorButton>
        )}
      </div>
      <div className="LocalEmulationControl-playbackRow">
        <IconButton
          label="Jump to start"
          icon="skip-prev"
          disabled={simPlayCanReverse || currentFrame.animationRunning}
          onClick={() => currentFrame.setCurrentTime(0)}
        />
        <IconButton
          label="Fast backward"
          icon="rewind"
          disabled={simPlayCanReverse || currentFrame.animationRunning}
          onClick={() => toggleSimPlayback('fast-backward')}
        />
        {currentFrame.animationRunning ? (
          <IconButton
            label="Pause"
            icon="pause"
            disabled={!simPlayable}
            onClick={stopSimPlayback}
          />
        ) : (
          <IconButton
            label="Play"
            icon="play"
            disabled={!simPlayable}
            className="LocalEmulationControl-playSimulationBtn"
            onClick={() => toggleSimPlayback('play')}
          />
        )}
        <IconButton
          label="Fast forward"
          icon="forward"
          disabled={!simPlayable || currentFrame.animationRunning}
          onClick={() => toggleSimPlayback('fast-forward')}
        />
        <IconButton
          label="Jump to end"
          icon="skip-next"
          disabled={!simPlayable || currentFrame.animationRunning}
          onClick={() => currentFrame.setCurrentTime(simulatedMaxTime)}
        />
      </div>
      <span className="LocalEmulationControl-credit">
        Powered by the <strong>Quantum Flytrap</strong> engine
      </span>
    </div>
  );
}
